import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import * as XLSX from 'xlsx';
import { useUserData } from '../contexts/UserDataContext';
import { useAdmin } from '../contexts/AdminContext';
import Button from '../components/Button';

const ResultsContainer = styled.div`
  min-height: 80vh;
  padding: ${props => props.theme.spacing.xl} 0;
`;

const Container = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  padding: 0 ${props => props.theme.spacing.md};
`;

const ResultsCard = styled.div`
  background: ${props => props.theme.colors.cardBackground || props.theme.colors.white};
  border-radius: ${props => props.theme.borderRadius.xl};
  padding: ${props => props.theme.spacing.xl};
  box-shadow: ${props => props.theme.shadows.lg};
  margin-bottom: ${props => props.theme.spacing.xl};
`;

const ResultsTitle = styled.h1`
  text-align: center;
  margin-bottom: ${props => props.theme.spacing.xl};
  color: ${props => props.theme.colors.darkBlue};
`;

const ResultsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: ${props => props.theme.spacing.lg};
  margin-bottom: ${props => props.theme.spacing.xl};
`;

const ResultCard = styled.div`
  background: ${props => props.theme.colors.cardBackground || props.theme.colors.white};
  border-radius: ${props => props.theme.borderRadius.lg};
  padding: ${props => props.theme.spacing.lg};
  text-align: center;
  border: 2px solid ${props => props.theme.colors.gray};
  transition: all 0.3s ease;
  
  &:hover {
    border-color: ${props => props.theme.colors.blue};
    transform: translateY(-2px);
    box-shadow: ${props => props.theme.shadows.md};
  }
`;

const ResultValue = styled.div`
  font-size: 2rem;
  font-weight: bold;
  color: ${props => props.theme.colors.darkBlue};
  margin-bottom: ${props => props.theme.spacing.sm};
`;

const ResultLabel = styled.div`
  font-size: 1rem;
  color: ${props => props.theme.colors.darkGray};
  font-weight: 500;
`;

const ComparisonSection = styled.div`
  background: ${props => props.theme.colors.containerBackground || props.theme.colors.lightGray};
  border-radius: ${props => props.theme.borderRadius.lg};
  padding: ${props => props.theme.spacing.lg};
  margin: ${props => props.theme.spacing.lg} 0;
`;

const ComparisonTitle = styled.h3`
  color: ${props => props.theme.colors.darkBlue};
  margin-bottom: ${props => props.theme.spacing.md};
  text-align: center;
`;

const ComparisonGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${props => props.theme.spacing.md};
`;

const ComparisonItem = styled.div`
  text-align: center;
  padding: ${props => props.theme.spacing.md};
  background: ${props => props.theme.colors.cardBackground || props.theme.colors.white};
  border-radius: ${props => props.theme.borderRadius.md};
`;

const ComparisonValue = styled.div`
  font-size: 1.5rem;
  font-weight: bold;
  color: ${props => props.theme.colors.blue};
  margin-bottom: ${props => props.theme.spacing.xs};
`;

const ComparisonLabel = styled.div`
  font-size: 0.9rem;
  color: ${props => props.theme.colors.darkGray};
`;

const DetailsSection = styled.div`
  background: ${props => props.theme.colors.lightGray};
  border-radius: ${props => props.theme.borderRadius.lg};
  padding: ${props => props.theme.spacing.lg};
  margin: ${props => props.theme.spacing.lg} 0;
`;

const DetailsTitle = styled.h3`
  color: ${props => props.theme.colors.darkBlue};
  margin-bottom: ${props => props.theme.spacing.md};
  text-align: center;
  font-size: 1.3rem;
`;

const DetailsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: ${props => props.theme.spacing.md};
  margin-top: ${props => props.theme.spacing.md};
`;

const DetailItem = styled.div`
  background: ${props => props.theme.colors.white};
  border-radius: ${props => props.theme.borderRadius.md};
  padding: ${props => props.theme.spacing.md};
  border-left: 4px solid ${props => props.theme.colors.blue};
`;

const DetailLabel = styled.div`
  font-size: 0.85rem;
  color: ${props => props.theme.colors.darkGray};
  margin-bottom: ${props => props.theme.spacing.xs};
  font-weight: 500;
`;

const DetailValue = styled.div`
  font-size: 1.1rem;
  color: ${props => props.theme.colors.darkBlue};
  font-weight: bold;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.md};
  justify-content: center;
  margin-top: ${props => props.theme.spacing.xl};
  flex-wrap: wrap;
`;

const ResultsPage: React.FC = () => {
  const { userData, pensionResult } = useUserData();
  const { addUsageReport } = useAdmin();
  const navigate = useNavigate();
  const reportAdded = useRef(false);

  const downloadReport = () => {
    if (!pensionResult) return;

    // Prepare report data
    const reportData = [
      { 'Parametr': 'Wysokość rzeczywista', 'Wartość': `${pensionResult.actualAmount.toLocaleString('pl-PL')} PLN` },
      { 'Parametr': 'Wysokość urealniona (z inflacją)', 'Wartość': `${pensionResult.realAmount.toLocaleString('pl-PL')} PLN` },
      { 'Parametr': 'Stopa zastąpienia', 'Wartość': `${pensionResult.replacementRate}%` },
      { 'Parametr': 'Średnia emerytura w roku przejścia', 'Wartość': `${pensionResult.averagePensionInRetirementYear.toLocaleString('pl-PL')} PLN` },
      { 'Parametr': 'Rok przejścia na emeryturę', 'Wartość': userData.workEndYear },
      { 'Parametr': 'Wiek', 'Wartość': userData.age },
      { 'Parametr': 'Płeć', 'Wartość': userData.gender === 'male' ? 'Mężczyzna' : 'Kobieta' },
      { 'Parametr': 'Wynagrodzenie brutto', 'Wartość': `${userData.grossSalary.toLocaleString('pl-PL')} PLN` },
      { 'Parametr': 'Zgromadzone środki', 'Wartość': `${(userData.accumulatedFunds || 0).toLocaleString('pl-PL')} PLN` },
      { 'Parametr': 'Oczekiwana emerytura', 'Wartość': `${userData.expectedPension.toLocaleString('pl-PL')} PLN` }
    ];

    // Create worksheet
    const ws = XLSX.utils.json_to_sheet(reportData);

    // Create workbook
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Raport emerytury');

    // Generate filename with current date
    const filename = `raport_emerytury_${new Date().toLocaleDateString('pl-PL').replace(/\./g, '-')}.xlsx`;

    // Save file
    XLSX.writeFile(wb, filename);
  };


  useEffect(() => {
    if (!pensionResult) {
      navigate('/simulation');
      return;
    }

    // Add usage report only once
    if (!reportAdded.current) {
      addUsageReport({
        dateOfUse: new Date().toLocaleDateString('pl-PL'),
        timeOfUse: new Date().toLocaleTimeString('pl-PL'),
        expectedPension: userData.expectedPension,
        age: userData.age,
        gender: userData.gender,
        salaryAmount: userData.grossSalary,
        includeSickLeave: userData.includeSickLeave,
        accumulatedFunds: userData.accumulatedFunds || 0,
        actualPension: pensionResult.actualAmount,
        realPension: pensionResult.realAmount,
        postalCode: userData.postalCode
      });
      reportAdded.current = true;
    }
  }, [pensionResult, userData, addUsageReport, navigate]);

  if (!pensionResult) {
    return null;
  }

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6 }
    }
  };

  return (
    <ResultsContainer>
      <Container>
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <ResultsCard>
            <ResultsTitle>Wyniki symulacji emerytury</ResultsTitle>
            
            <ResultsGrid>
              <motion.div variants={itemVariants}>
                <ResultCard>
                  <ResultValue>{pensionResult.actualAmount.toLocaleString('pl-PL')} PLN</ResultValue>
                  <ResultLabel>Wysokość rzeczywista</ResultLabel>
                </ResultCard>
              </motion.div>
              
              <motion.div variants={itemVariants}>
                <ResultCard>
                  <ResultValue>{pensionResult.realAmount.toLocaleString('pl-PL')} PLN</ResultValue>
                  <ResultLabel>Wysokość urealniona (z uwzględnieniem inflacji)</ResultLabel>
                </ResultCard>
              </motion.div>
              
              <motion.div variants={itemVariants}>
                <ResultCard>
                  <ResultValue>{pensionResult.replacementRate}%</ResultValue>
                  <ResultLabel>Stopa zastąpienia</ResultLabel>
                </ResultCard>
              </motion.div>
            </ResultsGrid>

            <motion.div variants={itemVariants}>
              <ComparisonSection>
                <ComparisonTitle>Porównanie z średnią emeryturą</ComparisonTitle>
                <ComparisonGrid>
                  <ComparisonItem>
                    <ComparisonValue>{pensionResult.actualAmount.toLocaleString('pl-PL')} PLN</ComparisonValue>
                    <ComparisonLabel>Twoja emerytura</ComparisonLabel>
                  </ComparisonItem>
                  <ComparisonItem>
                    <ComparisonValue>{pensionResult.averagePensionInRetirementYear.toLocaleString('pl-PL')} PLN</ComparisonValue>
                    <ComparisonLabel>Średnia emerytura w {userData.workEndYear}</ComparisonLabel>
                  </ComparisonItem>
                  <ComparisonItem>
                    <ComparisonValue>
                      {((pensionResult.actualAmount / pensionResult.averagePensionInRetirementYear) * 100).toFixed(1)}%
                    </ComparisonValue>
                    <ComparisonLabel>Stosunek do średniej</ComparisonLabel>
                  </ComparisonItem>
                </ComparisonGrid>
              </ComparisonSection>
            </motion.div>

            {pensionResult.calculationDetails && (
              <motion.div variants={itemVariants}>
                <DetailsSection>
                  <DetailsTitle>📊 Szczegóły obliczeń - Uwzględnione czynniki ekonomiczne</DetailsTitle>

                  <DetailsGrid>
                    <DetailItem>
                      <DetailLabel>Lata pracy</DetailLabel>
                      <DetailValue>{pensionResult.calculationDetails.workYears} lat</DetailValue>
                    </DetailItem>

                    <DetailItem>
                      <DetailLabel>Wiek emerytalny</DetailLabel>
                      <DetailValue>{pensionResult.calculationDetails.retirementAge} lat</DetailValue>
                    </DetailItem>

                    <DetailItem>
                      <DetailLabel>Składka emerytalna (całkowita)</DetailLabel>
                      <DetailValue>{pensionResult.calculationDetails.contributionRate}%</DetailValue>
                    </DetailItem>

                    <DetailItem>
                      <DetailLabel>Składki na konto główne (7,3%)</DetailLabel>
                      <DetailValue>{pensionResult.calculationDetails.mainAccountContributions.toLocaleString('pl-PL')} PLN</DetailValue>
                    </DetailItem>

                    <DetailItem>
                      <DetailLabel>Składki na subkonto (12,22%)</DetailLabel>
                      <DetailValue>{pensionResult.calculationDetails.subAccountContributions.toLocaleString('pl-PL')} PLN</DetailValue>
                    </DetailItem>

                    <DetailItem>
                      <DetailLabel>Suma składek emerytalnych</DetailLabel>
                      <DetailValue>{pensionResult.calculationDetails.totalContributions.toLocaleString('pl-PL')} PLN</DetailValue>
                    </DetailItem>

                    <DetailItem>
                      <DetailLabel>Kapitał zgromadzony</DetailLabel>
                      <DetailValue>{pensionResult.calculationDetails.accumulatedCapital.toLocaleString('pl-PL')} PLN</DetailValue>
                    </DetailItem>

                    <DetailItem>
                      <DetailLabel>Kapitał po waloryzacji</DetailLabel>
                      <DetailValue>{pensionResult.calculationDetails.valorizedCapital.toLocaleString('pl-PL')} PLN</DetailValue>
                    </DetailItem>

                    <DetailItem>
                      <DetailLabel>Średni wzrost PKB (okres pracy)</DetailLabel>
                      <DetailValue>{pensionResult.calculationDetails.averageGDPGrowth}%</DetailValue>
                    </DetailItem>

                    <DetailItem>
                      <DetailLabel>Średnia inflacja (okres pracy)</DetailLabel>
                      <DetailValue>{pensionResult.calculationDetails.averageInflation}%</DetailValue>
                    </DetailItem>

                    <DetailItem>
                      <DetailLabel>Średnie dalsze trwanie życia</DetailLabel>
                      <DetailValue>{pensionResult.calculationDetails.lifeExpectancy} lat</DetailValue>
                    </DetailItem>

                    <DetailItem>
                      <DetailLabel>Dzielnik miesięczny (GUS)</DetailLabel>
                      <DetailValue>{pensionResult.calculationDetails.monthlyDivisor} miesięcy</DetailValue>
                    </DetailItem>

                    <DetailItem>
                      <DetailLabel>Kwota wolna od podatku</DetailLabel>
                      <DetailValue>{pensionResult.calculationDetails.taxFreeAmount.toLocaleString('pl-PL')} PLN</DetailValue>
                    </DetailItem>

                    <DetailItem>
                      <DetailLabel>Składka zdrowotna</DetailLabel>
                      <DetailValue>{pensionResult.calculationDetails.healthInsuranceRate}%</DetailValue>
                    </DetailItem>
                  </DetailsGrid>

                  <div style={{ marginTop: '1.5rem', padding: '1rem', background: 'rgba(59, 130, 246, 0.1)', borderRadius: '8px' }}>
                    <p style={{ fontSize: '0.9rem', color: '#1e40af', margin: 0, textAlign: 'center' }}>
                      <strong>Formuła obliczenia:</strong> Emerytura = Kapitał po waloryzacji ÷ Średnie dalsze trwanie życia (w miesiącach)
                    </p>
                    <p style={{ fontSize: '0.85rem', color: '#6b7280', margin: '0.5rem 0 0 0', textAlign: 'center' }}>
                      Waloryzacja uwzględnia prognozy PKB i inflacji do roku {userData.workEndYear}
                    </p>
                  </div>
                </DetailsSection>
              </motion.div>
            )}

            <motion.div variants={itemVariants}>
              <ButtonGroup>
                <Button
                  variant="outline"
                  onClick={() => navigate('/simulation')}
                >
                  Zmień parametry
                </Button>
                <Button
                  variant="primary"
                  onClick={() => navigate('/dashboard')}
                >
                  Przejdź do Dashboard
                </Button>
                <Button
                  variant="secondary"
                  onClick={downloadReport}
                >
                  Pobierz raport
                </Button>
              </ButtonGroup>
            </motion.div>
          </ResultsCard>
        </motion.div>
      </Container>
    </ResultsContainer>
  );
};

export default ResultsPage;
